import React, { KeyboardEvent, useRef, useEffect } from 'react';
import { useCopilot } from '../hooks/use-copilot';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  onStopGeneration: () => void;
  placeholder?: string;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  isLoading,
  onStopGeneration,
  placeholder = 'Enter a prompt here',
}) => {
  const { inputMessage, setInputMessage } = useCopilot();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (inputMessage.trim() === '') return;

    onSendMessage(inputMessage);
    setInputMessage('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [inputMessage]);

  // Auto-focus the textarea when the component mounts
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
    }
  }, []);

  return (
    <div className="bg-white p-4">
      <form onSubmit={handleSubmit} className="mb-2">
        <div className="max-w-md mx-auto">
          <div className="relative flex items-center">
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={isLoading}
              className="w-full bg-gray-50 rounded-3xl py-3.5 pl-5 pr-12 focus:outline-none focus:ring-0 border-0 resize-none min-h-[52px] max-h-[120px] transition-all duration-200 text-base placeholder-gray-500"
              rows={1}
            />

            {isLoading ? (
              <button
                type="button"
                onClick={onStopGeneration}
                className="absolute right-2 p-1 rounded-md text-danger hover:bg-red-50 transition-colors duration-200"
                aria-label="Stop generation"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={inputMessage.trim() === ''}
                className="absolute right-2 p-1 rounded-md text-primary hover:bg-blue-50/50 transition-colors duration-200 disabled:opacity-40 disabled:hover:bg-transparent"
                aria-label="Send message"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13"></path>
                  <path d="M22 2L15 22L11 13L2 9L22 2Z"></path>
                </svg>
              </button>
            )}
          </div>
        </div>
      </form>

      <div className="text-xs text-gray-400 text-center max-w-md mx-auto">
        AltoGPT can make mistakes, so double-check it.
        <a href="https://www.altotech.ai/app-store/alto-gpt" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-1">
          Learn more
        </a>
      </div>
    </div>
  );
};
